import { netConfig, dateConfig } from '../config';
import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { message } from 'antd';

/**
 * 应用初始化结果接口
 */
export interface InitResult {
  success: boolean;
  error?: string;
}

/**
 * 应用初始化函数
 * 处理全局配置、API设置以及其他环境初始化
 */
export async function initializeApp(): Promise<InitResult> {
  try {
    console.log('🔧 开始初始化应用...');

    // 初始化网络配置
    netConfig.setup();

    // 配置全局axios默认值
    axios.defaults.baseURL = netConfig.apiConfig.BASE_URL + netConfig.apiConfig.API_PREFIX;
    axios.defaults.timeout = netConfig.apiConfig.TIMEOUT;

    // 设置全局拦截器
    setupGlobalInterceptors();

    // 初始化日期配置
    dateConfig.setup();

    // 加载初始数据（如果需要）
    await loadInitialData();

    // 设置其他全局环境
    setupGlobalEnvironment();

    console.log('✅ 应用初始化完成');
    return { success: true };
  } catch (error) {
    console.error('❌ 应用初始化失败:', error);
    let errorMessage = '应用初始化失败';

    if (error instanceof Error) {
      errorMessage = `${errorMessage}: ${error.message}`;
    }

    return {
      success: false,
      error: errorMessage
    };
  }
}

/**
 * 设置全局拦截器
 * 处理请求/响应的通用逻辑
 */
function setupGlobalInterceptors(): void {
  // 全局请求计数器（用于显示加载状态）
  let pendingRequests = 0;
  let loadingTimer: ReturnType<typeof setTimeout> | null = null;

  // 请求拦截器
  axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // 增加计数
      pendingRequests++;

      // 如果是首个请求，延迟显示加载状态，防止闪烁
      if (pendingRequests === 1 && !loadingTimer) {
        loadingTimer = setTimeout(() => {
          // 可以在这里触发全局loading
          // dispatch(setLoading(true)) 或其他全局状态管理
        }, 300); // 300ms延迟
      }

      // 添加认证头 - 统一使用 mslogin_token
      const token = localStorage.getItem('mslogin_token') ||
        sessionStorage.getItem('mslogin_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error: AxiosError) => {
      handleRequestComplete();
      return Promise.reject(error);
    }
  );

  // 响应拦截器
  axios.interceptors.response.use(
    (response: AxiosResponse) => {
      handleRequestComplete();
      return response;
    },
    (error: AxiosError) => {
      handleRequestComplete();

      // 错误处理逻辑
      if (error.response) {
        const status = error.response.status;

        switch (status) {
        case 401:
          // 未授权 - 显示错误信息
          message.error('请求未授权');
          break;

        case 403:
          message.error('没有权限执行此操作');
          break;

        case 404:
          message.error('请求的资源不存在');
          break;

        case 500:
          message.error('服务器错误，请稍后再试');
          break;

        default:
          message.error(`请求失败: ${error.message}`);
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        message.error('无法连接到服务器，请检查网络');
      } else {
        // 请求设置时发生错误
        message.error(`请求错误: ${error.message}`);
      }

      return Promise.reject(error);
    }
  );

  /**
   * 处理请求完成
   * 减少计数器并在适当时候关闭加载状态
   */
  function handleRequestComplete(): void {
    pendingRequests--;

    if (pendingRequests <= 0) {
      pendingRequests = 0;

      if (loadingTimer) {
        clearTimeout(loadingTimer);
        loadingTimer = null;
      }

      // 关闭全局loading
      // dispatch(setLoading(false)) 或其他全局状态管理
    }
  }
}

/**
 * 设置其他全局环境
 */
function setupGlobalEnvironment(): void {
  // 设置全局错误处理
  window.onerror = (message, source, lineno, colno, error) => {
    console.error('全局错误:', { message, source, lineno, colno, error });
    // 可以将错误发送到监控系统
    return false; // 允许默认的错误处理
  };

  // 其他全局设置...
  console.log('🌐 全局环境设置完成');
}

/**
 * 加载初始数据
 */
async function loadInitialData(): Promise<void> {
  try {
    // 这里可以加载应用初始需要的数据
    // 例如: 用户信息、权限、系统配置等

    // const userInfo = await axios.get('/user/info');
    // const permissions = await axios.get('/user/permissions');
    // const sysConfig = await axios.get('/system/config');

    // 可以将这些数据存储到全局状态中

  } catch (error) {
    console.error('加载初始数据失败:', error);
    throw error; // 重新抛出错误让上层处理
  }
}